import type { Observable } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";

import { blocks$ } from "~/stores/blocks";
import { boards$ } from "~/stores/boards";
import { datasets$ } from "~/stores/datasets";
import type { Dataset } from "~/stores/datasets/datasets.types";
import type { ToolSubtitleProps } from "./constants";

// Indexed view over the datasets observable. The inferred `syncedCrudPersist`
// type leaves the conditional `Record<...>` key generic unresolved at this
// call site, so TS rejects `datasets$[id]`. Casting to a typed Record keeps
// path-based subscriptions (and matches how datasets$ is keyed at runtime).
const datasetsById$ = datasets$ as unknown as Observable<
  Record<string, Dataset>
>;

/**
 * Renders the agent-supplied `title` from a tool's input as the call's
 * subtitle (e.g. "Run query · Top customers by revenue Q3"). Used by any
 * tool whose input schema includes a `title` field.
 */
export const InputTitleSubtitle = ({ input }: ToolSubtitleProps) => {
  const title = (input as { title?: string } | undefined)?.title;
  return title ? title : null;
};

/**
 * Subtitle for `runQuery`: the agent's title plus a result hint once output
 * lands. Because the result table renders collapsed inside the fold by default,
 * the hint ("42 rows" / "failed") tells the user there is data to expand.
 */
export const RunQuerySubtitle = ({ input, output }: ToolSubtitleProps) => {
  const title = (input as { title?: string } | undefined)?.title;
  const out = output as { totalRows?: number; error?: string } | undefined;
  const hint = out?.error
    ? "failed"
    : typeof out?.totalRows === "number"
      ? `${out.totalRows} row${out.totalRows === 1 ? "" : "s"}`
      : null;
  return [title, hint].filter(Boolean).join(" · ") || null;
};

/**
 * Subtitle for `createChart`: the agent's title plus the chart type (or
 * "failed" when the config didn't validate against the run's columns).
 */
export const CreateChartSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const title = (input as { title?: string } | undefined)?.title;
  const chartType = (input as { graphConfig?: { type?: string } } | undefined)
    ?.graphConfig?.type;
  const out = output as { error?: string } | undefined;
  const hint = out?.error ? "failed" : (chartType ?? null);
  return [title, hint].filter(Boolean).join(" · ") || null;
};

/**
 * Resolves a dataset name from the store by `datasetId` in tool input,
 * falling back to `output.name`.
 */
export const DatasetByIdSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const datasetId = (input as { datasetId?: string } | undefined)?.datasetId;
  const storeName = useValue(() =>
    datasetId ? datasetsById$[datasetId]?.name.get() : undefined,
  );
  const name =
    storeName ?? (output as { name?: string } | undefined)?.name ?? undefined;
  return name ? name : null;
};

/**
 * For tools where datasetId isn't in the input (e.g. updateDataset uses
 * requestContext) — resolves name from `output.id` or `output.name`.
 */
export const DatasetFromOutputSubtitle = ({ output }: ToolSubtitleProps) => {
  const outputId = (output as { id?: string } | undefined)?.id;
  const storeName = useValue(() =>
    outputId ? datasetsById$[outputId]?.name.get() : undefined,
  );
  const name =
    (output as { name?: string } | undefined)?.name ?? storeName ?? undefined;
  return name ? name : null;
};

/**
 * Resolves a board name from the store by `boardId` in tool input,
 * falling back to `output.name`.
 */
export const BoardByIdSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const boardId = (input as { boardId?: string } | undefined)?.boardId;
  const storeName = useValue(() =>
    boardId ? boards$[boardId]?.name.get() : undefined,
  );
  const name =
    storeName ?? (output as { name?: string } | undefined)?.name ?? undefined;
  return name ? name : null;
};

/**
 * For createBoard — name comes from `input.name` or `output.name` (no
 * boardId in input).
 */
export const CreateBoardSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const name =
    (input as { name?: string } | undefined)?.name ??
    (output as { name?: string } | undefined)?.name;
  return name ? name : null;
};

/**
 * Subtitle for the unified read-only `blockTool`. Renders only the affected
 * entity name (block name for `read`, board name or "{N} blocks" for
 * `list`). The action verb lives in the title, set by `getBlockToolTitle`.
 */
export const BlockToolSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as
    | { action?: string; blockId?: string; boardId?: string }
    | undefined;
  const blockId = inputObj?.blockId;
  const boardId = inputObj?.boardId;
  const blockName = useValue(() =>
    blockId ? blocks$[blockId]?.name.get() : undefined,
  );
  const boardName = useValue(() =>
    boardId ? boards$[boardId]?.name.get() : undefined,
  );

  if (inputObj?.action === "read") {
    const fallback = (output as { config?: { name?: string } } | undefined)
      ?.config?.name;
    return blockName ?? fallback ?? null;
  }
  if (inputObj?.action === "list") {
    if (boardName) return boardName;
    const total = (output as { total?: number } | undefined)?.total;
    return total != null ? `${total} blocks` : null;
  }
  return null;
};

/**
 * Resolves the dynamic title for `blockTool` based on `input.action`,
 * e.g. "Read block", "List blocks". Returns "Block" as a fallback while
 * the action hasn't streamed yet.
 */
export const getBlockToolTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "read") return "Read block";
  if (action === "list") return "List blocks";
  return "Block";
};

/**
 * Subtitle for the unified mutating `blockMutateTool`. Renders only the
 * affected block name — the action verb lives in the title, set by
 * `getBlockMutateTitle`. For `create`, the name comes from `output.name`
 * (only available after the agent finishes); for the others it comes from
 * the legend store via `blockId`.
 */
export const BlockMutateSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as { action?: string; blockId?: string } | undefined;
  const blockId = inputObj?.blockId;
  const blockName = useValue(() =>
    blockId ? blocks$[blockId]?.name.get() : undefined,
  );

  if (inputObj?.action === "create") {
    const outputName = (output as { name?: string } | undefined)?.name;
    return outputName ?? null;
  }
  return blockName ?? null;
};

/**
 * Resolves the dynamic title for `blockMutateTool` based on `input.action`,
 * e.g. "Create block", "Update block". Returns "Block" as a fallback while
 * the action hasn't streamed yet.
 */
export const getBlockMutateTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  switch (action) {
    case "create":
      return "Create block";
    case "update":
      return "Update block";
    case "delete":
      return "Delete block";
    case "duplicate":
      return "Duplicate block";
    case "move":
      return "Move block";
    default:
      return "Block";
  }
};

/**
 * Subtitle for the unified read-only `boardTool`. For `read`, resolves the
 * board name from the store via `boardId`; for `list`, falls back to a count
 * from `output.total`.
 */
export const BoardToolSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as { action?: string; boardId?: string } | undefined;
  const boardId = inputObj?.boardId;
  const boardName = useValue(() =>
    boardId ? boards$[boardId]?.name.get() : undefined,
  );

  if (inputObj?.action === "read") {
    const fallback = (output as { name?: string } | undefined)?.name;
    return boardName ?? fallback ?? null;
  }
  if (inputObj?.action === "list") {
    const total = (output as { total?: number } | undefined)?.total;
    return total != null ? `${total} boards` : null;
  }
  return null;
};

/**
 * Resolves the dynamic title for `boardTool` based on `input.action`.
 */
export const getBoardToolTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "read") return "Read board";
  if (action === "list") return "List boards";
  return "Board";
};

/**
 * Subtitle for the unified mutating `boardMutateTool`. For `create`, the
 * name comes from `input.name` or `output.name` (the agent supplies the
 * name). For the others it comes from the store via `boardId`.
 */
export const BoardMutateSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as
    | { action?: string; boardId?: string; name?: string }
    | undefined;
  const boardId = inputObj?.boardId;
  const boardName = useValue(() =>
    boardId ? boards$[boardId]?.name.get() : undefined,
  );

  if (inputObj?.action === "create") {
    const outputName = (output as { name?: string } | undefined)?.name;
    return inputObj?.name ?? outputName ?? null;
  }
  return boardName ?? null;
};

/**
 * Resolves the dynamic title for `boardMutateTool` based on `input.action`.
 */
export const getBoardMutateTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  switch (action) {
    case "create":
      return "Create board";
    case "update":
      return "Update board";
    case "delete":
      return "Delete board";
    case "duplicate":
      return "Duplicate board";
    default:
      return "Board";
  }
};

/**
 * Subtitle for the unified read-only `datasetTool`. For `readQuery`, resolves
 * the dataset name from the store via `datasetId`; for `list`, falls back to
 * a count from `output.total`.
 */
export const DatasetToolSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as { action?: string; datasetId?: string } | undefined;
  const datasetId = inputObj?.datasetId;
  const datasetName = useValue(() =>
    datasetId ? datasetsById$[datasetId]?.name.get() : undefined,
  );

  if (inputObj?.action === "readQuery") {
    const fallback = (output as { name?: string } | undefined)?.name;
    return datasetName ?? fallback ?? null;
  }
  if (inputObj?.action === "list") {
    const total = (output as { total?: number } | undefined)?.total;
    return total != null ? `${total} datasets` : null;
  }
  return null;
};

/**
 * Resolves the dynamic title for `datasetTool` based on `input.action`.
 */
export const getDatasetToolTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "readQuery") return "Read dataset";
  if (action === "list") return "List datasets";
  return "Dataset";
};

/**
 * Subtitle for the unified mutating `datasetMutateTool`. For `create`, the
 * name comes from `input.title` (chat-UI subtitle) or `input.name` /
 * `output.name`. For `update` and `updateQuery`, the dataset operates on
 * `requestContext.datasetId`, so we resolve the name from the store via the
 * output `id` / `datasetId`.
 */
export const DatasetMutateSubtitle = ({ input, output }: ToolSubtitleProps) => {
  const inputObj = input as
    | { action?: string; title?: string; name?: string }
    | undefined;
  const outputObj = output as
    | { id?: string; datasetId?: string; name?: string }
    | undefined;
  const outputId = outputObj?.id ?? outputObj?.datasetId;
  const storeName = useValue(() =>
    outputId ? datasetsById$[outputId]?.name.get() : undefined,
  );

  if (inputObj?.action === "create") {
    return inputObj?.title ?? inputObj?.name ?? outputObj?.name ?? null;
  }
  return outputObj?.name ?? storeName ?? null;
};

/**
 * Resolves the dynamic title for `datasetMutateTool` based on `input.action`.
 */
export const getDatasetMutateTitle = ({ input }: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  switch (action) {
    case "create":
      return "Create dataset";
    case "update":
      return "Update dataset";
    case "updateQuery":
      return "Update query";
    default:
      return "Dataset";
  }
};

/**
 * Subtitle for the unified read-only `boardFilterTool`. For `list`, resolves
 * the parent board name from the store via `boardId`, falling back to a
 * `${total} filters` count from the output.
 */
export const BoardFilterToolSubtitle = ({
  input,
  output,
}: ToolSubtitleProps) => {
  const inputObj = input as { action?: string; boardId?: string } | undefined;
  const boardId = inputObj?.boardId;
  const boardName = useValue(() =>
    boardId ? boards$[boardId]?.name.get() : undefined,
  );

  if (inputObj?.action === "list") {
    if (boardName) return boardName;
    const total = (output as { total?: number } | undefined)?.total;
    return total != null ? `${total} filters` : null;
  }
  return null;
};

/**
 * Resolves the dynamic title for `boardFilterTool` based on `input.action`.
 */
export const getBoardFilterToolTitle = ({
  input,
}: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "list") return "List filters";
  return "Board filter";
};

/**
 * Subtitle for the unified mutating `boardFilterMutateTool`. Filters can be
 * unnamed; falling back to null is fine since the title carries the verb.
 */
export const BoardFilterMutateSubtitle = ({
  input,
  output,
}: ToolSubtitleProps) => {
  const inputName = (input as { name?: string } | undefined)?.name;
  const outputName = (output as { name?: string } | undefined)?.name;
  return inputName ?? outputName ?? null;
};

/**
 * Resolves the dynamic title for `boardFilterMutateTool` based on
 * `input.action`.
 */
export const getBoardFilterMutateTitle = ({
  input,
}: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  switch (action) {
    case "create":
      return "Create filter";
    case "update":
      return "Update filter";
    case "delete":
      return "Delete filter";
    default:
      return "Board filter";
  }
};

/**
 * Subtitle for the unified read-only `datasourceTool`. For `read`, returns
 * the datasource's name; for `list`, falls back to a `${total} datasources`
 * count.
 */
export const DatasourceToolSubtitle = ({
  input,
  output,
}: ToolSubtitleProps) => {
  const inputObj = input as { action?: string } | undefined;
  const outputObj = output as { name?: string; total?: number } | undefined;

  if (inputObj?.action === "read") {
    return outputObj?.name ?? null;
  }
  if (inputObj?.action === "list") {
    const total = outputObj?.total;
    return total != null ? `${total} datasources` : null;
  }
  return null;
};

/**
 * Resolves the dynamic title for `datasourceTool` based on `input.action`.
 */
export const getDatasourceToolTitle = ({
  input,
}: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "list") return "List datasources";
  if (action === "read") return "Read datasource";
  return "Datasource";
};

/**
 * Subtitle for the unified mutating `datasourceMutateTool`. The update
 * result doesn't carry a `name`; falling back to null is fine since the
 * title carries the verb.
 */
export const DatasourceMutateSubtitle = ({ output }: ToolSubtitleProps) => {
  const outputId = (output as { id?: string } | undefined)?.id;
  return outputId ?? null;
};

/**
 * Resolves the dynamic title for `datasourceMutateTool` based on
 * `input.action`.
 */
export const getDatasourceMutateTitle = ({
  input,
}: ToolSubtitleProps): string => {
  const action = (input as { action?: string } | undefined)?.action;
  if (action === "update") return "Update datasource";
  return "Datasource";
};

/**
 * Subtitle for the `askUserTool` — shows the agent-supplied form `title`,
 * falling back to the first question's prompt or a `${N} questions` count.
 */
export const AskUserToolSubtitle = ({ input }: ToolSubtitleProps) => {
  const inputObj = input as
    | {
        title?: string;
        questions?: Array<{ prompt?: string }>;
      }
    | undefined;
  if (inputObj?.title) return inputObj.title;
  const questions = inputObj?.questions ?? [];
  if (questions.length === 1) return questions[0]?.prompt ?? null;
  if (questions.length > 1) return `${questions.length} questions`;
  return null;
};
